import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  Trash2,
  Check,
  Archive,
  Inbox,
  Mail,
  Phone,
  Building2,
  Clock,
  ShieldCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "oohma_admin_token";

const statusColors = {
  new: { bg: "rgba(255,90,31,0.14)", fg: "#ff7a45" },
  handled: { bg: "rgba(34,197,94,0.14)", fg: "#4ade80" },
  archived: { bg: "rgba(148,163,184,0.14)", fg: "#94a3b8" },
};

const Login = ({ onAuth }) => {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setLoading(true);
    try {
      await axios.get(`${API}/contact`, { headers: { "X-Admin-Token": token } });
      localStorage.setItem(TOKEN_KEY, token);
      onAuth(token);
    } catch (err) {
      toast.error("Invalid access token.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <form onSubmit={submit} className="oo-card" style={{ padding: 40, width: "100%", maxWidth: 420 }}>
        <div className="inline-flex items-center justify-center" style={{ width: 54, height: 54, borderRadius: 14, background: "var(--oo-orange-soft)" }}>
          <ShieldCheck size={26} className="text-accent" />
        </div>
        <h1 className="font-display" style={{ fontSize: 26, fontWeight: 700, marginTop: 20 }}>Admin Access</h1>
        <p style={{ color: "var(--oo-muted)", fontSize: 14, marginTop: 8 }}>Enter your access token to view enquiries.</p>
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Access token"
          style={{ width: "100%", marginTop: 24, background: "var(--oo-bg)", border: "1px solid var(--oo-border)", borderRadius: 12, padding: "14px 16px", color: "var(--oo-text)", fontSize: 15, outline: "none" }}
        />
        <button type="submit" disabled={loading} className="btn-primary" style={{ marginTop: 20, width: "100%", justifyContent: "center" }}>
          {loading ? "Checking..." : "Enter Dashboard"}
        </button>
      </form>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const load = async (tk = token) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contact`, { headers: { "X-Admin-Token": tk } });
      setItems(res.data);
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
        setToken("");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAuth = (tk) => {
    setToken(tk);
    load(tk);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contact/${id}`, { status }, { headers });
      setItems((arr) => arr.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await axios.delete(`${API}/contact/${id}`, { headers });
      setItems((arr) => arr.filter((it) => it.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setItems([]);
  };

  if (!authed) return <Login onAuth={onAuth} />;

  const counts = {
    all: items.length,
    new: items.filter((i) => i.status === "new").length,
    handled: items.filter((i) => i.status === "handled").length,
    archived: items.filter((i) => i.status === "archived").length,
  };
  const visible = filter === "all" ? items : items.filter((i) => i.status === filter);

  const fmt = (d) => new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

  return (
    <div style={{ minHeight: "100vh", paddingTop: 40, paddingBottom: 60 }}>
      <div className="oo-container">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span className="oo-eyebrow">OOHMA Admin</span>
            <h1 className="font-display" style={{ fontSize: "clamp(28px,4vw,44px)", fontWeight: 800, marginTop: 8 }}>Enquiry Dashboard</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={() => load()} className="btn-ghost" style={{ padding: "11px 20px", fontSize: 14 }}>
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} className="btn-ghost" style={{ padding: "11px 20px", fontSize: 14 }}>
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>

        {/* Filters / counts */}
        <div className="flex flex-wrap gap-3" style={{ marginTop: 28 }}>
          {[
            { key: "all", label: "All", icon: Inbox },
            { key: "new", label: "New", icon: Mail },
            { key: "handled", label: "Handled", icon: Check },
            { key: "archived", label: "Archived", icon: Archive },
          ].map((t) => {
            const Icon = t.icon;
            const on = filter === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className="font-display inline-flex items-center gap-2"
                style={{
                  fontSize: 14, fontWeight: 600, padding: "10px 18px", borderRadius: 999, cursor: "pointer",
                  border: "1px solid " + (on ? "var(--oo-orange)" : "var(--oo-border)"),
                  background: on ? "var(--oo-orange)" : "transparent",
                  color: on ? "#fff" : "var(--oo-muted)", transition: "all 0.2s",
                }}
              >
                <Icon size={15} /> {t.label} <span style={{ opacity: 0.8 }}>({counts[t.key]})</span>
              </button>
            );
          })}
        </div>

        {/* List */}
        <div className="grid gap-4" style={{ marginTop: 28 }}>
          {visible.length === 0 && (
            <div className="oo-card" style={{ padding: 40, textAlign: "center", color: "var(--oo-muted)" }}>
              No enquiries here yet.
            </div>
          )}
          {visible.map((it) => {
            const sc = statusColors[it.status] || statusColors.new;
            return (
              <div key={it.id} className="oo-card" style={{ padding: 24 }}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display" style={{ fontSize: 19, fontWeight: 700 }}>{it.name}</h3>
                      <span className="font-display" style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", padding: "4px 10px", borderRadius: 999, background: sc.bg, color: sc.fg }}>{it.status}</span>
                      {it.email_sent && (
                        <span style={{ fontSize: 12, color: "var(--oo-faint)" }}>✉ emailed</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1" style={{ marginTop: 10, color: "var(--oo-muted)", fontSize: 14 }}>
                      <a href={`mailto:${it.email}`} className="inline-flex items-center gap-1.5 hover-underline"><Mail size={14} className="text-accent" /> {it.email}</a>
                      {it.phone && <a href={`tel:${it.phone}`} className="inline-flex items-center gap-1.5 hover-underline"><Phone size={14} className="text-accent" /> {it.phone}</a>}
                      {it.company && <span className="inline-flex items-center gap-1.5"><Building2 size={14} className="text-accent" /> {it.company}</span>}
                      <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-accent" /> {fmt(it.created_at)}</span>
                    </div>
                    {it.type && <p style={{ marginTop: 10, fontSize: 13, color: "var(--oo-faint)" }}>Enquiry type: <span style={{ color: "var(--oo-text)" }}>{it.type}</span></p>}
                  </div>
                  <div className="flex gap-2">
                    {it.status !== "handled" && (
                      <button onClick={() => setStatus(it.id, "handled")} title="Mark handled" style={iconBtn}><Check size={16} /></button>
                    )}
                    {it.status !== "archived" && (
                      <button onClick={() => setStatus(it.id, "archived")} title="Archive" style={iconBtn}><Archive size={16} /></button>
                    )}
                    {it.status !== "new" && (
                      <button onClick={() => setStatus(it.id, "new")} title="Mark new" style={iconBtn}><Mail size={16} /></button>
                    )}
                    <button onClick={() => remove(it.id)} title="Delete" style={{ ...iconBtn, color: "#f87171", borderColor: "rgba(248,113,113,0.4)" }}><Trash2 size={16} /></button>
                  </div>
                </div>
                <p style={{ marginTop: 16, color: "var(--oo-text)", lineHeight: 1.7, fontSize: 15, whiteSpace: "pre-wrap", background: "var(--oo-bg)", border: "1px solid var(--oo-border)", borderRadius: 12, padding: 16 }}>{it.message}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const iconBtn = {
  width: 40,
  height: 40,
  borderRadius: 10,
  border: "1px solid var(--oo-border)",
  background: "transparent",
  color: "var(--oo-muted)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "all 0.2s",
};

export default Admin;
